import { Button, TooltipProvider } from "@capacit-com/lara-law-shared-ui";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@capacit-com/lara-law-shared-ui";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@capacit-com/lara-law-shared-ui";
import { ChevronDown, Pilcrow, ListTree } from "lucide-react";
import { ParagraphItem } from "@/partials/document-renderer/hooks/useParagraphExtraction";
import { DocumentToolbarGroup } from "@/partials/document-renderer/components/document-toolbar/DocumentToolbarGroup";
import { DocumentViewGroup } from "@/partials/document-renderer/components/document-toolbar/DocumentViewGroup";
import { DocumentFollowGroup } from "@/partials/document-renderer/components/document-toolbar/DocumentFollowGroup";
import { DocumentExportGroup } from "@/partials/document-renderer/components/document-toolbar/DocumentExportGroup";
import { toolbarTier } from "@/partials/document-renderer/components/document-toolbar/documentToolbarTiers";
import CurrentDocumentTypeIndicator from "@/partials/document-renderer/components/document-timeline/CurrentDocumentTypeIndicator";
import type { CurrentTimelineDocumentDisplay } from "@/partials/document-renderer/components/document-timeline/timelineNodeAppearance";
import { DocumentSearchControls } from "@/components/document/DocumentSearchControls";
import LegislationGuidanceLinksButton from "./LegislationGuidanceLinksButton";
import ConsolidationVersionSelect from "./ConsolidationVersionSelect";
import LegislationSubscribeModal from "@/components/modals/LegislationSubscribeModal";
import { useFavoriteToggle } from "@/hooks/useFavoriteToggle";
import { useSubscribeAction } from "@/hooks/useSubscribeAction";
import { cn } from "@/lib/utils";
import type { DocumentJumpNavigationKind } from "@/lib/tocUtils";
import { DocumentCategory } from "@/models/legislation/Legislation";
import { SubscriptionCategory } from "@/models/subscriptions/Subscription";
import { useState, useEffect, useRef } from "react";

interface DocumentSearchProps {
  query: string;
  setQuery: (query: string) => void;
  currentIndex: number;
  totalMatches: number;
  goToNext: () => void;
  goToPrevious: () => void;
  clearSearch: () => void;
}

export type LegislationFavoriteSubscribeProps = {
  externalId: string;
  datasource: string;
  title: string;
  internalId?: string;
  legislationExternalId?: string | null;
  publishedOn?: string | null;
  isCurrent?: boolean | null;
  documentTypeId?: string | number | null;
  isFavorite: boolean;
  isSubscribed: boolean;
  legislationSubscriptionId?: string;
  isReady: boolean;
};

export type ConsolidationVersionProps = {
  /** Distinct activation dates (ISO `yyyy-MM-dd`), sorted ascending. */
  activationDates: string[];
  /**
   * Priority-ordered candidates for the currently rendered date; the first one
   * matching an activation date is selected.
   */
  selectedDateCandidates: ReadonlyArray<string | null>;
  onSelectDate: (date: string) => void;
  /** The law itself is no longer in force (it carries the historic watermark). */
  isHistoric?: boolean;
};

type DocumentHeaderProps = {
  currentDocumentTypeDisplay?: CurrentTimelineDocumentDisplay | null;
  /** When set, shows the "version:" activation-date dropdown next to the type badge. */
  consolidationVersion?: ConsolidationVersionProps | null;
  onPrint: () => void;
  /** When set, shows a "Download original PDF" button that opens this URL. */
  originalPdfUrl?: string | null;
  // Optional paragraph navigation props
  paragraphItems?: ParagraphItem[];
  onParagraphSelect?: (id: string) => void;
  // Optional search props (always shown when provided)
  searchProps?: DocumentSearchProps;
  // Feature flag for paragraph navigation (only for supported sources)
  showParagraphNavigation?: boolean;
  /**
   * What `paragraphItems` holds. Documents without § structure (vejledninger and
   * friends) pass their numbered section outline, which needs its own copy — and
   * room for real titles rather than "§ 12".
   */
  paragraphNavigationKind?: DocumentJumpNavigationKind;
  /**
   * External id of the open legislation document. Powers one-click access to
   * the guidance ("vejledning") that belongs to it; unset while the document is
   * still being processed.
   */
  documentId?: string;
  // Zoom controls props
  zoomLevel?: number;
  onZoomChange?: (level: number) => void;
  onFitToWidth?: () => void;
  // Comparison mode props (only shown when provided)
  isCompareMode?: boolean;
  onToggleCompareMode?: () => void;
  favoriteSubscribe?: LegislationFavoriteSubscribeProps;
};

const LegislationDocumentHeader: React.FC<DocumentHeaderProps> = ({
  currentDocumentTypeDisplay = null,
  consolidationVersion = null,
  onPrint,
  originalPdfUrl,
  paragraphItems = [],
  onParagraphSelect,
  searchProps,
  showParagraphNavigation = false,
  paragraphNavigationKind = "paragraph",
  documentId,
  zoomLevel,
  onZoomChange,
  onFitToWidth,
  isCompareMode,
  onToggleCompareMode,
  favoriteSubscribe,
}) => {
  const [paragraphPopoverOpen, setParagraphPopoverOpen] = useState(false);
  const [selectedParagraph, setSelectedParagraph] = useState<string | null>(
    null
  );
  const savedScrollPosition = useRef<number>(0);
  const shouldScrollToSelected = useRef<boolean>(false);

  const entityPath = favoriteSubscribe
    ? `legislation/${favoriteSubscribe.externalId}/datasource/${favoriteSubscribe.datasource}`
    : "";

  const favorite = useFavoriteToggle({
    documentCategory: DocumentCategory.Legislation,
    documentId: favoriteSubscribe?.externalId ?? "",
    title: favoriteSubscribe?.title ?? "",
    isFavorite: favoriteSubscribe?.isFavorite ?? false,
    datasource: favoriteSubscribe?.datasource,
    internalId: favoriteSubscribe?.internalId,
  });

  const legislationMetadata = favoriteSubscribe
    ? {
        datasource: favoriteSubscribe.datasource,
        legislationExternalId: favoriteSubscribe.legislationExternalId,
        publishedOn: favoriteSubscribe.publishedOn,
        isCurrent: favoriteSubscribe.isCurrent,
        documentTypeId: favoriteSubscribe.documentTypeId,
      }
    : undefined;

  const subscribe = useSubscribeAction({
    subscribeCategory: SubscriptionCategory.Legislation,
    entityPath,
    entityName: favoriteSubscribe?.title ?? "",
    source: "legislation",
    isSubscribed: favoriteSubscribe?.isSubscribed ?? false,
    legislationExternalId: favoriteSubscribe?.externalId,
    legislationDatasource: favoriteSubscribe?.datasource,
    legislationSubscriptionId: favoriteSubscribe?.legislationSubscriptionId,
    legislationMetadata,
  });

  const isSectionNavigation = paragraphNavigationKind === "section";
  const NavigationIcon = isSectionNavigation ? ListTree : Pilcrow;
  const navigationTriggerLabel = isSectionNavigation
    ? "Gå til afsnit"
    : "Gå til §";
  const navigationSearchPlaceholder = isSectionNavigation
    ? "Søg afsnit..."
    : "Søg paragraf...";

  const handleParagraphSelect = (id: string, label: string) => {
    setSelectedParagraph(label);
    onParagraphSelect?.(id);
    setParagraphPopoverOpen(false);
    // Mark that we should scroll to the selected item next time
    shouldScrollToSelected.current = true;
  };

  // Save scroll position when popover closes
  useEffect(() => {
    if (!paragraphPopoverOpen) {
      const commandElement = document.querySelector(
        '[data-command-id="paragraph-navigation"]'
      );
      if (commandElement) {
        const scrollableContainer = commandElement.querySelector(
          '[data-slot="command-list"]'
        ) as HTMLElement | null;
        if (scrollableContainer) {
          savedScrollPosition.current = scrollableContainer.scrollTop;
        }
      }
    }
  }, [paragraphPopoverOpen]);

  // Restore scroll position or scroll to selected paragraph when popover opens
  useEffect(() => {
    if (paragraphPopoverOpen) {
      const timeoutId = setTimeout(() => {
        const commandElement = document.querySelector(
          '[data-command-id="paragraph-navigation"]'
        );
        if (commandElement) {
          const scrollableContainer = commandElement.querySelector(
            '[data-slot="command-list"]'
          ) as HTMLElement | null;

          if (scrollableContainer) {
            // If we should scroll to selected item (after selecting a paragraph)
            if (shouldScrollToSelected.current && selectedParagraph) {
              shouldScrollToSelected.current = false;
              const allItems = commandElement.querySelectorAll("[cmdk-item]");
              const itemsArray = Array.from(allItems) as HTMLElement[];
              const foundItem = itemsArray.find((item) => {
                const itemText = item.textContent?.trim();
                return itemText === selectedParagraph;
              });

              if (foundItem) {
                const itemTop = foundItem.offsetTop;
                const itemHeight = foundItem.offsetHeight;
                const containerHeight = scrollableContainer.clientHeight;
                const scrollPosition =
                  itemTop - containerHeight / 2 + itemHeight / 2;

                savedScrollPosition.current = Math.max(0, scrollPosition);
              }
            }

            // Restore the saved scroll position instantly
            scrollableContainer.scrollTop = savedScrollPosition.current;
          }
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [paragraphPopoverOpen, selectedParagraph]);

  return (
    <TooltipProvider>
      <DocumentToolbarGroup
        label="Find og gå til"
        segmented={false}
        className="shrink-0"
      >
        {searchProps && (
          <DocumentSearchControls
            searchProps={searchProps}
            inputId="document-search-input"
            triggerId="document-search-trigger"
            previousButtonId="search-previous-btn"
            nextButtonId="search-next-btn"
          />
        )}

        {showParagraphNavigation && paragraphItems.length > 0 && (
          <Popover
            open={paragraphPopoverOpen}
            onOpenChange={setParagraphPopoverOpen}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7 shrink-0 gap-1 px-2"
                id="paragraph-navigation-trigger"
              >
                {selectedParagraph ? (
                  <span className="max-w-40 truncate">{selectedParagraph}</span>
                ) : (
                  <>
                    <NavigationIcon className="size-3.5" />
                    <span className={toolbarTier.fromLabelsInline}>
                      {navigationTriggerLabel}
                    </span>
                  </>
                )}
                <ChevronDown className="size-3.5 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className={cn("p-0", isSectionNavigation ? "w-80" : "w-48")}
              align="start"
            >
              <Command data-command-id="paragraph-navigation">
                <CommandInput placeholder={navigationSearchPlaceholder} />
                <CommandList>
                  <CommandEmpty>Ingen resultater</CommandEmpty>
                  <CommandGroup>
                    {paragraphItems.map((item) => (
                      <CommandItem
                        key={item.id}
                        value={item.label}
                        onSelect={() =>
                          handleParagraphSelect(item.id, item.label)
                        }
                        className={
                          item.label === selectedParagraph ? "bg-accent" : ""
                        }
                      >
                        {item.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}

        <LegislationGuidanceLinksButton documentId={documentId} />
      </DocumentToolbarGroup>

      <span className={toolbarTier.spacer} aria-hidden="true" />

      <DocumentToolbarGroup label="Dokument" className="w-auto min-w-0 shrink">
        {/* The date is what the reader came for, so the type gives way
              first — ten times as fast as the date under pressure, and
              entirely once the toolbar is compact, where its shape would cost
              more room than it explains. The tooltip and the document itself
              still say what the type is. */}
        <CurrentDocumentTypeIndicator
          display={currentDocumentTypeDisplay}
          className={cn(
            "max-w-40 shrink-[10] group-data-[tier-full]/toolbar:max-w-56",
            toolbarTier.fromMediumInlineFlex
          )}
        />
        {consolidationVersion && (
          <ConsolidationVersionSelect
            activationDates={consolidationVersion.activationDates}
            selectedDateCandidates={consolidationVersion.selectedDateCandidates}
            onSelectDate={consolidationVersion.onSelectDate}
            isHistoric={consolidationVersion.isHistoric}
          />
        )}
      </DocumentToolbarGroup>

      <span className={toolbarTier.spacer} aria-hidden="true" />

      {zoomLevel !== undefined && onZoomChange && (
        <DocumentViewGroup
          zoomLevel={zoomLevel}
          onZoomChange={onZoomChange}
          onFitToWidth={onFitToWidth}
          compare={
            onToggleCompareMode
              ? { isCompareMode, onToggleCompareMode }
              : undefined
          }
          idPrefix="legislation"
        />
      )}

      {favoriteSubscribe && (
        <DocumentFollowGroup
          isReady={favoriteSubscribe.isReady}
          isFavorite={favoriteSubscribe.isFavorite}
          isSubscribed={favoriteSubscribe.isSubscribed}
          favoriteLabel={favorite.label}
          subscribeLabel={subscribe.label}
          favoriteLoading={favorite.isLoading}
          subscribeLoading={subscribe.isLoading}
          onFavorite={() => {
            void favorite.toggle();
          }}
          onSubscribe={() => {
            void subscribe.activate();
          }}
          idPrefix="legislation"
        />
      )}

      <DocumentExportGroup
        onPrint={onPrint}
        originalPdfUrl={originalPdfUrl}
        idPrefix="legislation"
      />

      {favoriteSubscribe && (
        <LegislationSubscribeModal
          open={subscribe.isLegislationModalOpen}
          onOpenChange={subscribe.setIsLegislationModalOpen}
          entityName={favoriteSubscribe.title}
          legislationExternalId={favoriteSubscribe.externalId}
          legislationDatasource={favoriteSubscribe.datasource}
          legislationMetadata={legislationMetadata}
          isConfirmLoading={subscribe.isLoading}
          onConfirm={subscribe.confirmLegislationSubscribe}
        />
      )}
    </TooltipProvider>
  );
};

export default LegislationDocumentHeader;
